"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface CheckoutLoadingContextValue {
  loadingPriceId: string | null;
  isCheckoutBusy: boolean;
  beginStripeCheckout: (priceId: string) => void;
  endStripeCheckout: () => void;
}

const CheckoutLoadingContext = createContext<
  CheckoutLoadingContextValue | undefined
>(undefined);

export function CheckoutLoadingProvider({ children }: { children: ReactNode }) {
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);

  const beginStripeCheckout = useCallback((priceId: string) => {
    setLoadingPriceId(priceId);
  }, []);

  const endStripeCheckout = useCallback(() => {
    setLoadingPriceId(null);
  }, []);

  const value = useMemo<CheckoutLoadingContextValue>(
    () => ({
      loadingPriceId,
      isCheckoutBusy: loadingPriceId !== null,
      beginStripeCheckout,
      endStripeCheckout,
    }),
    [loadingPriceId, beginStripeCheckout, endStripeCheckout]
  );

  return (
    <CheckoutLoadingContext.Provider value={value}>
      {children}
    </CheckoutLoadingContext.Provider>
  );
}

export function useCheckoutLoading(): CheckoutLoadingContextValue {
  const ctx = useContext(CheckoutLoadingContext);
  if (!ctx) {
    throw new Error(
      "useCheckoutLoading must be used within a CheckoutLoadingProvider"
    );
  }
  return ctx;
}
